import * as React from "react";
import { connect } from "react-redux";
import {
    CalendarState,
    CalendarDrawerSettings,
    DateMetaData,
    CalendarDrawerContent,
    SpiralItem,
    RubberBandItem,
    ImageDetails,
    CustomImage,
    CalendarPreviewDrawerSettings,
    BurdeFontResponse,
} from "burde";
import { drawCoverPreview } from "svg/draw/draw-cover-preview";
import * as styles from "./summary.css";
import * as d3 from "d3";
import { BaseType } from "d3";
import { drawCalendar } from "svg/draw/draw-calendar";
import { actionCreators } from "page/calendar/store";
import { ApplicationState } from "store";
import {
    actionCreators as uiActionCreators,
    getFastTrackStatus,
} from "ui/store";
import {
    getSelectedLayout,
    getSelectedDayModuleElements,
    getSelectedWeekModuleElements,
    getMetadata,
    actionCreators as burdeContentActionCreators,
    getFontOptions,
} from "burde-api-content/store";
import {
    DayModuleElement,
    SvgCalendarLayout,
    WeekModuleElement,
} from "ui/constants";
import { getLayoutById } from "ui/service";
import { useEffect, useState } from "react";
import { useResources } from "contexts/resource-context";
import { CalendarSizes } from "svg/constants";

type CalendarSummaryProps = {
    calendar: CalendarState;
    customCoverImage: CustomImage | null;
    customBackImage: CustomImage | null;
    spiral: SpiralItem | null;
    rubberband: RubberBandItem | null;
    fastTrackStatus: boolean;
    selectedLayout: SvgCalendarLayout | null;
    selectedDayModuleElements: DayModuleElement[];
    selectedWeekModuleElements: WeekModuleElement[];
    metadata: DateMetaData[] | null;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    fontOptions: BurdeFontResponse | null;
    borderImage: ImageDetails | null;
} & typeof actionCreators &
    typeof uiActionCreators &
    typeof burdeContentActionCreators;

const CalendarSummary: React.FC<CalendarSummaryProps> = (props) => {
    const resources = useResources();
    const [selectedTab, setSelectedTab] = useState(0);
    const [divRef, setDivRef] = useState<HTMLDivElement | null>(null);

    const drawCover = (props: CalendarSummaryProps) => {
        if (!divRef) {
            return;
        }

        const {
            calendar,
            selectedLayout,
            selectedDayModuleElements,
            selectedWeekModuleElements,
            metadata,
            customCoverImage,
            customBackImage,
            spiral,
            rubberband,
            coverImage,
            backCoverImage,
            fontOptions,
            borderImage,
        } = props;

        if (selectedTab !== 1) {
            const calendarPreviewDrawerSettings: CalendarPreviewDrawerSettings = {
                isFrontPage: selectedTab === 0,
                withAnimation: false,
                withPlasticCover:
                    calendar.calendarSize.toLowerCase() !== CalendarSizes.B5, //NOTE: Refactor this to be more dynamic in the future
                showCalendarName: false,
            };

            const calendarDrawerContent: CalendarDrawerContent = {
                article: null,
                calendarId: null,
                layout: null,
                dayModules: null,
                weekModules: null,
                metadata: [],
                coverImage: coverImage,
                backCoverImage: backCoverImage,
                customCoverImage: customCoverImage,
                customBackImage: customBackImage,
                borderImage: null,
                extraPages: null,
                salesPages: null,
                customerInformation: null,
                ownerPage: null,
                monthDivider: null,
                ownerPageImageUrl: "",
                weekNumberPrefix: resources["Header_WeekNumberPrefix"],
                spiral: spiral,
                rubberband: rubberband,
                calendarName: null,
                fonts: fontOptions,
            };

            drawCoverPreview(
                calendar,
                calendarPreviewDrawerSettings,
                calendarDrawerContent,
                divRef,
                () => {
                    let svg = d3
                        .select(divRef as BaseType)
                        .select("svg") as any;
                    if (selectedTab === 0) {
                        stringToBlob(svg.node().outerHTML);
                    }
                },
            );
        } else {
            if (!selectedLayout) {
                return;
            }

            const calendarDrawerSettings: CalendarDrawerSettings = {
                isConfiguratorView: true,
                drawCoverOnly: false,
                drawInsertOnly: false,
                numberOfWeeks: 1,
                drawWeekModulePlaceholders: false,
            };

            const calendarDrawerContent: CalendarDrawerContent = {
                article: null,
                calendarId: null,
                layout: selectedLayout,
                dayModules: selectedDayModuleElements,
                weekModules: selectedWeekModuleElements,
                metadata: metadata ? metadata : [],
                coverImage: null,
                backCoverImage: null,
                customCoverImage: null,
                customBackImage: null,
                borderImage: borderImage,
                extraPages: null,
                salesPages: null,
                customerInformation: null,
                ownerPage: null,
                monthDivider: null,
                ownerPageImageUrl: "",
                weekNumberPrefix: resources["Header_WeekNumberPrefix"],
                spiral: spiral,
                rubberband: rubberband,
                calendarName: null,
                fonts: fontOptions,
            };

            drawCalendar(
                calendar,
                calendarDrawerSettings,
                calendarDrawerContent,
                divRef,
            );
        }
    };

    const stringToBlob = (str: string) => {
        var blob = new Blob([str], { type: "image/svg+xml" });
        props.updateThumbnailBlob(blob);
    };

    useEffect(() => {
        const { fastTrackStatus, SetFastTrack } = props;

        if (fastTrackStatus) {
            SetFastTrack(false);
        }

        d3.select("#sizeContainer").attr("class", styles.containerWidth);

        if (
            divRef &&
            props.spiral &&
            props.rubberband &&
            props.selectedLayout
        ) {
            drawCover(props);
        }
    }, [
        divRef,
        props.spiral,
        props.rubberband,
        props.selectedLayout,
        props.selectedDayModuleElements,
        props.selectedWeekModuleElements,
    ]);

    useEffect(() => {
        d3.select(divRef as BaseType)
            .transition()
            .duration(200)
            .delay(0)
            .style("opacity", "0")
            .style("transform", "scale(0.5)")
            .on("end", () => {
                d3.select("#sizeContainer").attr(
                    "class",
                    selectedTab === 1
                        ? styles.heightForInlay
                        : styles.containerWidth,
                );
                drawCover(props);
            })
            .transition()
            .duration(200)
            .delay(0)
            .style("opacity", "1")
            .style("transform", "scale(1)");
    }, [selectedTab]);

    return (
        <>
            <div
                style={{
                    display: "block",
                    margin: "20px 0 0 20px",
                    width: "80px",
                }}>
                <img src={"images/logotype.svg"} />
            </div>
            <div className={styles.previewAreaWrapper}>
                <div id="tabs" className={styles.previewTabContainer}>
                    <div
                        className={`${styles.previewTabItem} ${
                            selectedTab === 0
                                ? styles.previewTabItemSelected
                                : ""
                        }`}
                        onClick={() => {
                            if (selectedTab !== 0) {
                                setSelectedTab(0);
                            }
                        }}>
                        {resources["Calendar_Summary_Front"]}
                    </div>
                    <div
                        className={`${styles.previewTabItem} ${
                            selectedTab === 1
                                ? styles.previewTabItemSelected
                                : ""
                        }`}
                        onClick={() => {
                            if (selectedTab !== 1) {
                                setSelectedTab(1);
                            }
                        }}>
                        {resources["Calendar_Summary_Inside"]}
                    </div>
                    <div
                        className={`${styles.previewTabItem} ${
                            selectedTab === 2
                                ? styles.previewTabItemSelected
                                : ""
                        }`}
                        onClick={() => {
                            if (selectedTab !== 2) {
                                setSelectedTab(2);
                            }
                        }}>
                        {resources["Calendar_Summary_Back"]}
                    </div>
                </div>
                <div className={styles.previewArea}>
                    <div id={"sizeContainer"}>
                        <div
                            className={"svg-container"}
                            style={{
                                width: "100%",
                                height: "100%",
                            }}
                            ref={(divRef) => setDivRef(divRef)}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    fastTrackStatus: getFastTrackStatus(state),
    selectedLayout: getSelectedLayout(state),
    selectedDayModuleElements: getSelectedDayModuleElements(state),
    selectedWeekModuleElements: getSelectedWeekModuleElements(state),
    metadata: getMetadata(state),
    fontOptions: getFontOptions(state),
});
const mapDispatchToProps = {
    ...actionCreators,
    ...uiActionCreators,
    ...burdeContentActionCreators,
};

const ConnectedCalendarSummary = connect(
    mapStateToProps,
    mapDispatchToProps,
)(CalendarSummary);

export default ConnectedCalendarSummary;
